import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, ExternalLink, Image, Globe } from "lucide-react";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useSetupWizard } from "@/contexts/SetupWizardContext";
import { Aviso } from "./ui";
import type { PasoProps } from "./types";

/**
 * ÚLTIMO PASO: su enlace de reservas.
 *
 * Es el paso que cierra el círculo: el negocio acaba de montar lo que hacía
 * falta y aquí se le da la dirección que tiene que pegar en Instagram, en
 * Google y en su web, con un botón para probarla él mismo antes de repartirla.
 *
 * Si todavía le falta algo imprescindible (solo pasa en citas) se dice cuál y
 * se le lleva de vuelta a ese paso, en lugar de darle un enlace que va a
 * responder "no hay horas libres".
 */

const NOMBRE_PASO: Record<string, string> = {
  horario: "el horario",
  servicios: "los servicios",
  profesionales: "los profesionales",
  salas: "las salas",
  mesas: "las mesas",
};

export default function PasoEnlace({ estado, irAPaso }: PasoProps) {
  const { showToast } = useGlobalToast();
  const { cerrar } = useSetupWizard();
  const navigate = useNavigate();
  const [copiado, setCopiado] = useState(false);
  const esCitas = estado.bookingMode === "appointments";
  const enlace = estado.enlaceReservas;

  useEffect(() => {
    if (!copiado) return;
    const timer = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(timer);
  }, [copiado]);

  const copiar = async () => {
    if (!enlace) return;
    try {
      await navigator.clipboard.writeText(enlace);
      setCopiado(true);
      showToast("Enlace copiado");
    } catch {
      showToast("Copia el enlace a mano: tu navegador no nos deja hacerlo", "error");
    }
  };

  const irA = (ruta: string) => {
    cerrar();
    navigate(ruta);
  };

  const pendientes = estado.pasos.filter((p) => p.obligatorio && !p.hecho);

  return (
    <div className="space-y-4">
      {pendientes.length > 0 ? (
        <>
          <Aviso tono="ojo">
            Todavía te falta{" "}
            <strong>
              {pendientes.map((p) => NOMBRE_PASO[p.id] ?? p.id).join(" y ")}
            </strong>
            . Sin eso, quien abra tu enlace verá que no hay horas libres.
          </Aviso>
          <div className="flex flex-wrap gap-2">
            {pendientes.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => irAPaso(p.id)}
                className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
              >
                Terminar {NOMBRE_PASO[p.id] ?? p.id}
              </button>
            ))}
          </div>
        </>
      ) : (
        <Aviso tono="exito">
          <strong>
            Ya puedes recibir {esCitas ? "citas" : "reservas"} por internet.
          </strong>{" "}
          Este es tu enlace: pégalo en Instagram, en tu ficha de Google y en tu
          web, y tus clientes podrán reservar también cuando estés cerrado.
        </Aviso>
      )}

      {enlace && (
        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Tu enlace de {esCitas ? "citas" : "reservas"}
          </p>
          <p className="text-sm font-mono break-all text-gray-900">{enlace}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copiar}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiado ? "Copiado" : "Copiar enlace"}
            </button>
            <a
              href={enlace}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              Probarlo yo mismo
            </a>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
        <p className="text-sm font-medium text-gray-900 mb-2">
          Para que se vea bien (opcional, cuando quieras)
        </p>
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={() => irA("/locations")}
            className="w-full flex items-center gap-2 text-left text-sm text-gray-700 hover:text-primary-700"
          >
            <Image className="w-4 h-4 text-gray-400" />
            Sube una foto y completa la ficha de tu negocio
          </button>
          <button
            type="button"
            onClick={() => irA("/public-profile")}
            className="w-full flex items-center gap-2 text-left text-sm text-gray-700 hover:text-primary-700"
          >
            <Globe className="w-4 h-4 text-gray-400" />
            Publica tu página web, incluida en el plan
          </button>
          <button
            type="button"
            onClick={() => irA("/integration")}
            className="w-full flex items-center gap-2 text-left text-sm text-gray-700 hover:text-primary-700"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
            Meter el botón de reservar en tu web o sacar el código QR
          </button>
        </div>
      </div>
    </div>
  );
}
